import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { X } from 'lucide-react';
import AdminLink from './AdminLink';

const Navigation = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  
  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > 20) {
        setIsScrolled(true);
      } else {
        setIsScrolled(false);
      }
    };
    
    window.addEventListener('scroll', handleScroll);
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  return (
    <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
      isScrolled ? 'py-3 bg-black/80 backdrop-blur-lg shadow-md' : 'py-6 bg-transparent'
    }`}>
      <div className="container mx-auto px-4 md:px-6">
        <div className="flex items-center justify-between">
          <Link to="/" className="flex items-center" aria-label="Daryian Kelton Home">
            <img 
              src="/logo/DK Monogram Outline WH.svg" 
              alt="Daryian Kelton Logo" 
              className="h-12 mr-2"
              width="48"
              height="48"
            />
            <span className="text-xl font-bold">Daryian Kelton</span>
          </Link>
          
          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center space-x-8">
            <Link to="/about" className="nav-link" aria-label="About">About</Link>
            <Link to="/videos" className="nav-link" aria-label="Videos">Videos</Link>
            <Link to="/services" className="nav-link" aria-label="Services">Services</Link>
            <Link to="/contact" className="nav-link" aria-label="Contact">Contact</Link>
            <AdminLink />
            <a 
              href="https://seriousmattersnj.com" 
              target="_blank" 
              rel="noopener noreferrer" 
              className="serious-matters-link px-3 py-2"
              aria-label="Visit Serious Matters website"
            >
              Serious Matters
            </a>
          </div>
          
          {/* Animated Drumstick Menu Button */}
          <button 
            className="md:hidden w-12 h-12 flex items-center justify-center relative z-50 focus:outline-none mr-1"
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            aria-label={isMobileMenuOpen ? "Close menu" : "Open menu"}
            aria-expanded={isMobileMenuOpen}
          >
            <div className="block relative w-7 h-7">
              {/* Left drumstick */}
              <span 
                className={`block absolute h-0.5 w-7 transform transition-all duration-300 ease-in-out ${
                  isMobileMenuOpen 
                    ? 'rotate-45 translate-y-0 w-7 h-0.5 bg-client-yellow shadow-glow-sm' 
                    : 'rotate-0 -translate-y-1.5 bg-white'
                }`}
              />
              {/* Right drumstick */}
              <span 
                className={`block absolute h-0.5 w-7 transform transition-all duration-300 ease-in-out ${
                  isMobileMenuOpen 
                    ? '-rotate-45 translate-y-0 w-7 h-0.5 bg-client-yellow shadow-glow-sm' 
                    : 'rotate-0 translate-y-1.5 bg-white'
                }`}
              />
              {/* Drumstick tips when closed - these should completely disappear when open */}
              <span 
                className={`block absolute h-0.5 w-1.5 bg-white rounded-full left-0 transform transition-all duration-300 ease-in-out ${
                  isMobileMenuOpen ? 'opacity-0 scale-0' : '-translate-y-1.5 rotate-45 opacity-100 scale-100'
                }`}
              />
              <span 
                className={`block absolute h-0.5 w-1.5 bg-white rounded-full left-0 transform transition-all duration-300 ease-in-out ${
                  isMobileMenuOpen ? 'opacity-0 scale-0' : 'translate-y-1.5 -rotate-45 opacity-100 scale-100'
                }`}
              />
            </div>
          </button>
        </div>
      </div>
      
      {/* Mobile Menu */}
      {isMobileMenuOpen && (
        <div className="md:hidden fixed inset-0 top-[72px] bg-drummer-dark/98 backdrop-blur-xl z-50 flex flex-col overflow-y-auto">
          <div className="container mx-auto px-4 py-8">
            <div className="flex flex-col space-y-6">
              <Link 
                to="/about" 
                className="text-2xl font-bold hover:text-client-yellow transition-colors"
                onClick={() => setIsMobileMenuOpen(false)}
                aria-label="About"
              >
                About
              </Link>
              <Link 
                to="/videos" 
                className="text-2xl font-bold hover:text-client-yellow transition-colors"
                onClick={() => setIsMobileMenuOpen(false)}
                aria-label="Videos"
              >
                Videos
              </Link>
              <Link 
                to="/services" 
                className="text-2xl font-bold hover:text-client-yellow transition-colors"
                onClick={() => setIsMobileMenuOpen(false)}
                aria-label="Services"
              >
                Services
              </Link>
              <Link 
                to="/contact" 
                className="text-2xl font-bold hover:text-client-yellow transition-colors"
                onClick={() => setIsMobileMenuOpen(false)}
                aria-label="Contact"
              >
                Contact
              </Link>
              <AdminLink />
              <a 
                href="https://seriousmattersnj.com" 
                target="_blank" 
                rel="noopener noreferrer" 
                className="text-2xl font-bold hover:text-client-yellow transition-colors"
                onClick={() => setIsMobileMenuOpen(false)}
                aria-label="Visit Serious Matters website"
              >
                Serious Matters
              </a>
            </div>
          </div>
        </div>
      )}
    </nav>
  );
};

export default Navigation;
